/**
 * Inscrição estadual (IE) do destinatário da NF-e. CNPJ de contribuinte do ICMS sem IE na nota é recusado pela SEFAZ
 * ("IE do destinatário não informada"). A Spedy aceita três situações (`stateTaxNumberIndicator`):
 * - `taxPayer`: contribuinte do ICMS, com a IE em `stateTaxNumber`;
 * - `exempt`: contribuinte isento de inscrição estadual (a Spedy espera o texto "ISENTO" na IE);
 * - `nonTaxPayer`: não contribuinte (CPF sempre é; CNPJ sem IE é, não "isento").
 * Regra pura; cópia da tela em `src/utils/stateRegistration.ts`, conferida por teste. Só o formato é validado (2 a 14
 * dígitos): o dígito verificador muda a cada UF e quem confere de verdade é a SEFAZ.
 */
export type IeIndicator = "taxPayer" | "exempt" | "nonTaxPayer";

export interface RecipientIe {
  indicator: IeIndicator;
  /** Só dígitos; nulo em isento e não contribuinte. */
  number: string | null;
}

const onlyDigits = (value: unknown): string => String(value ?? "").replace(/\D/g, "");

/** Texto digitado/guardado ("ISENTO", "123.456.789.110", vazio) → situação; vazio ou inválido = null. */
export function parseStateRegistration(raw: unknown): RecipientIe | null {
  const text = String(raw ?? "").trim();
  if (!text) return null;
  if (/^isent[oa]$/i.test(text)) return { indicator: "exempt", number: null };
  if (/^n[aã]o[\s_-]*contribuinte$/i.test(text)) return { indicator: "nonTaxPayer", number: null };
  const digits = onlyDigits(text);
  if (digits.length >= 2 && digits.length <= 14 && /^[\d.\-/\s]+$/.test(text)) return { indicator: "taxPayer", number: digits };
  return null;
}

/**
 * Forma guardada no cadastro do cliente: só dígitos (contribuinte), "ISENTO" ou "NAO_CONTRIBUINTE". Vazio = ainda não
 * informado (é obrigatório para CNPJ, então vazio quer dizer que falta perguntar).
 */
export function stateRegistrationToStorage(ie: RecipientIe | null | undefined): string {
  if (!ie) return "";
  if (ie.indicator === "exempt") return "ISENTO";
  if (ie.indicator === "taxPayer") return ie.number ?? "";
  return "NAO_CONTRIBUINTE";
}

/**
 * Mensagem de erro, ou null se está tudo certo. CPF nunca exige IE. CNPJ exige que o fornecedor diga qual das três
 * situações vale (senão a nota seria recusada ou sairia com a situação errada).
 */
export function validateRecipientIe(args: {
  document: unknown;
  indicator: IeIndicator | null | undefined;
  number: unknown;
}): string | null {
  if (onlyDigits(args.document).length !== 14) return null;
  if (!args.indicator) {
    return "Informe a inscrição estadual do cliente, ou marque isento / não contribuinte.";
  }
  if (args.indicator === "taxPayer") {
    const digits = onlyDigits(args.number);
    if (digits.length < 2 || digits.length > 14) return "Inscrição estadual inválida: use de 2 a 14 números.";
  }
  return null;
}

/** Texto para mostrar a IE guardada: o número, "Isento", "Não contribuinte" ou "Não informada". */
export function describeStateRegistration(raw: unknown): string {
  const ie = parseStateRegistration(raw);
  if (!ie) return "Não informada";
  if (ie.indicator === "exempt") return "Isento";
  if (ie.indicator === "nonTaxPayer") return "Não contribuinte";
  return ie.number ?? "Não informada";
}
